/* eslint-disable no-underscore-dangle */
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import DriverExpensesService from '../Services/DriverExpensesService';
import TripClaimsService from '../Services/TripClaimsService';
import { overtimeEntries, overtimeEntryId } from '../Utils/driverOvertime';
import { countTripClaims, tripClaimsOf } from '../Utils/tripClaims';

// Presigned photo links live ~15 minutes. Past this age the list is
// re-fetched before a receipt is opened, so the admin never lands on an
// expired S3 "AccessDenied" page.
export const PHOTO_URL_FRESH_MS = 10 * 60 * 1000;

const errorText = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

/**
 * One booking's trip claims — its receipts (fetched) and its overtime entries
 * (on the booking) — with the one review path. Owned by Order Details and
 * shared by the banner and the Driver Events tab, so a decision made in one
 * shows in the other at once.
 *
 * `enabled` defers the receipts request (e.g. until the tab is opened, or
 * the booking is known to have claims).
 */
export default function useBookingClaims(order, { enabled = true } = {}) {
  const bookingId = order?._id;
  const [expenses, setExpenses] = useState([]);
  const [overtime, setOvertime] = useState(() => overtimeEntries(order?.driver_overtime));
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const fetchedAtRef = useRef(0);
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    setOvertime(overtimeEntries(order?.driver_overtime));
  }, [order?.driver_overtime]);

  const fetchExpenses = useCallback(async () => {
    if (!bookingId) return [];
    const mySeq = fetchSeqRef.current + 1;
    fetchSeqRef.current = mySeq;
    setLoading(true);
    try {
      const rows = await DriverExpensesService.listForBooking(bookingId);
      if (mySeq !== fetchSeqRef.current) return rows; // stale response
      setExpenses(rows);
      fetchedAtRef.current = Date.now();
      return rows;
    } catch (err) {
      if (mySeq === fetchSeqRef.current) setError(errorText(err, 'Failed to load driver expenses'));
      return [];
    } finally {
      if (mySeq === fetchSeqRef.current) {
        setLoading(false);
        setLoaded(true);
      }
    }
  }, [bookingId]);

  useEffect(() => {
    if (enabled) fetchExpenses();
  }, [enabled, fetchExpenses]);

  // Each review resolves to the claim's id on success, false on failure.
  // `orderLine` ({ operator_id }) approves AND files the cost as one call;
  // omitted, the cost is absorbed and only the decision is written.
  const reviewReceipt = async (expense, decision, orderLine) => {
    setError('');
    setBusyId(expense._id);
    try {
      const updated = await TripClaimsService.reviewReceipt(expense._id, decision, orderLine);
      setExpenses((prev) => prev.map((e) => (
        e._id === expense._id
          // Keep the already-signed photo link if the response omits it.
          ? { ...e, ...(updated || {}), status: updated?.status || decision }
          : e
      )));
      return expense._id;
    } catch (err) {
      setError(errorText(err, 'Failed to update the expense'));
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const reviewOvertime = async (entry, decision) => {
    const id = overtimeEntryId(entry);
    setError('');
    setBusyId(id);
    try {
      const updated = await TripClaimsService.reviewOvertime(bookingId, id, decision);
      setOvertime((prev) => prev.map((e) => (
        overtimeEntryId(e) === id
          ? {
            ...e,
            _id: updated?._id || e._id,
            review_status: updated?.review_status || decision,
            reviewed_by: updated?.reviewed_by ?? e.reviewed_by,
            reviewed_at: updated?.reviewed_at ?? e.reviewed_at,
          }
          : e
      )));
      // The entry's id from now on (an entry from before ids gets one).
      return String(updated?._id || id);
    } catch (err) {
      setError(errorText(err, 'Failed to update the overtime'));
      return false;
    } finally {
      setBusyId(null);
    }
  };

  // The photo link is presigned and short-lived. If the list is older than
  // PHOTO_URL_FRESH_MS, re-fetch first and open the fresh link. The blank tab
  // is opened synchronously so the popup blocker treats it as a user click.
  const openPhoto = async (event, expense) => {
    if (Date.now() - fetchedAtRef.current < PHOTO_URL_FRESH_MS) return;
    event.preventDefault();
    const tab = window.open('', '_blank');
    const rows = await fetchExpenses();
    const fresh = rows.find((e) => e._id === expense._id)?.photo_url;
    if (!tab) return;
    if (fresh) {
      tab.opener = null;
      tab.location.href = fresh;
    } else {
      tab.close();
    }
  };

  // Until the receipts are in, the list's own count stands in for them.
  const counts = useMemo(() => {
    if (loaded) return countTripClaims(expenses, overtime);
    const live = countTripClaims([], overtime);
    const receipts = tripClaimsOf(order).receipts || 0;
    return { receipts, overtime: live.overtime, total: receipts + live.overtime };
  }, [loaded, expenses, overtime, order]);

  return {
    bookingId,
    expenses,
    overtime,
    loading,
    loaded,
    error,
    setError,
    busyId,
    counts,
    reviewReceipt,
    reviewOvertime,
    openPhoto,
    refetch: fetchExpenses,
  };
}
